// File: src/components/admin/UserListView.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschränkt)
// All rights reserved.
//
// User List View component for the Admin Portal

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, TextField, Button, CircularProgress, Alert,
    InputAdornment, Card, Snackbar, Chip, FormControlLabel, Switch
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Search, CloudDownload as DownloadIcon } from '@mui/icons-material';
import { downloadAsJson } from '../../utils/downloadUtils';
// Note: These imports assume an AuthContext exists
// import { useAuth } from '../../app/AuthContext';
import { adminApi, handleAdminApiError } from '../../services/adminApi';

const UserListView = () => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cursor, setCursor] = useState(null);
    const [isListComplete, setIsListComplete] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [includeArchived, setIncludeArchived] = useState(false);

    // const { signOut } = useAuth();
    const navigate = useNavigate();

    const loadInitialUsers = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await adminApi.getUsers({ includeArchived });
            setUsers(data.users || []);
            setCursor(data.cursor);
            setIsListComplete(data.list_complete);
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setError(errorInfo);
            // if (errorInfo.type === 'FORBIDDEN' || errorInfo.type === 'UNAUTHORIZED') {
            //     setTimeout(() => signOut(), 3000);
            // }
        } finally {
            setLoading(false);
        }
    }, [includeArchived]);

    useEffect(() => {
        loadInitialUsers();
    }, [loadInitialUsers]);

    const handleLoadMore = async () => {
        if (loadingMore || isListComplete) return;
        try {
            setLoadingMore(true);
            setError(null);
            const data = await adminApi.getUsers({ cursor, includeArchived });
            setUsers(prevUsers => {
                const existingUids = new Set(prevUsers.map(u => u.uid));
                const uniqueNewUsers = (data.users || []).filter(u => !existingUids.has(u.uid));
                return [...prevUsers, ...uniqueNewUsers];
            });
            setCursor(data.cursor);
            setIsListComplete(data.list_complete);
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setError(errorInfo);
        } finally {
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredUsers(users);
        } else {
            const lowercasedTerm = searchTerm.toLowerCase();
            setFilteredUsers(users.filter(user =>
                (user.uid?.toLowerCase().includes(lowercasedTerm)) ||
                (user.email?.toLowerCase().includes(lowercasedTerm)) ||
                (user.displayName?.toLowerCase().includes(lowercasedTerm))
            ));
        }
    }, [searchTerm, users]);

    const columns = [
        { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
        { field: 'uid', headerName: 'User ID', flex: 1, minWidth: 250 },
        { field: 'displayName', headerName: 'Name', flex: 1, minWidth: 150 },
        {
            field: 'status',
            headerName: 'Status',
            width: 120,
            renderCell: (params) => {
                const status = params.row.status || 'active';
                return (
                    <Chip
                        label={status === 'disabled' ? 'Disabled' : 'Active'}
                        color={status === 'disabled' ? 'default' : 'success'}
                        size="small"
                        variant={status === 'disabled' ? 'outlined' : 'filled'}
                    />
                );
            },
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            renderCell: (params) => (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(`/users/${params.row.uid}`)}
                >
                    View
                </Button>
            ),
        },
    ];

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error.message}
                </Alert>
            )}

            <Card sx={{ mt: 2 }}>
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                        <TextField
                            fullWidth
                            placeholder="Search users by email, name, or UID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={includeArchived}
                                    onChange={(e) => setIncludeArchived(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Show Archived"
                            sx={{ minWidth: 150 }}
                        />
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => {
                                const metadata = searchTerm ? { filters: { searchTerm } } : {};
                                downloadAsJson(filteredUsers, 'users', metadata);
                            }}
                            disabled={filteredUsers.length === 0}
                            data-testid="users-list-btn-download"
                            sx={{ minWidth: 150 }}
                        >
                            Download JSON
                        </Button>
                    </Box>

                    <DataGrid
                        rows={filteredUsers}
                        columns={columns}
                        getRowId={(row) => row.uid}
                        pageSize={25}
                        rowsPerPageOptions={[25, 50, 100]}
                        disableSelectionOnClick
                        autoHeight
                        onRowDoubleClick={(params) => navigate(`/users/${params.row.uid}`)}
                        getRowClassName={(params) =>
                            params.row.status === 'disabled' ? 'row-disabled' : ''
                        }
                        sx={{
                            '& .row-disabled': {
                                opacity: 0.6,
                                bgcolor: 'action.disabledBackground',
                            },
                        }}
                    />

                    {!isListComplete && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Button
                                variant="outlined"
                                onClick={handleLoadMore}
                                disabled={loadingMore}
                            >
                                {loadingMore ? <CircularProgress size={20} /> : 'Load More'}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Card>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={snackbar.message}
            />
        </Box>
    );
};

export default UserListView;

